import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { ArrowLeft, KeyRound, Mail } from "lucide-react";
import React, { useState } from "react";

interface ForgotPasswordFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSuccess,
  onSwitchToLogin,
}) => {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"email" | "reset">("email");

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await resetPassword({ username: email });
      setStep("reset");
    } catch (err: any) {
      console.error("Reset password error:", err);
      if (err.message.includes("User does not exist")) {
        setError("No account found with this email address.");
      } else if (err.message.includes("Attempt limit exceeded")) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to send reset code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword,
      });

      onSuccess();
    } catch (err: any) {
      console.error("Confirm reset password error:", err);
      if (err.message.includes("Invalid verification code")) {
        setError(
          "Invalid verification code. Please check your email and try again."
        );
      } else if (err.message.includes("Attempt limit exceeded")) {
        setError("Too many attempts. Please request a new code.");
      } else {
        setError(err.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "reset") {
    return (
      <div className="min-h-screen bg-earth-50 flex flex-col justify-center items-center py-12 px-4">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="mx-auto w-14 h-14 bg-field rounded-full flex items-center justify-center shadow-card mb-5">
            <KeyRound className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl text-earth-900">
            Set New Password
          </h1>
          <p className="mt-2 text-earth-600">
            Enter the code sent to{" "}
            <span className="font-semibold text-earth-900">{email}</span>
          </p>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-sm card p-8 animate-slide-up">
          {error && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-alert-urgent text-sm text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label htmlFor="verificationCode" className="label">
                Verification Code
              </label>
              <input
                id="verificationCode"
                name="verificationCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                required
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="input text-center tracking-widest font-mono text-lg"
                placeholder="000000"
                maxLength={6}
              />
              <p className="mt-2 text-xs text-earth-500 text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            <div>
              <label htmlFor="newPassword" className="label">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="Enter new password"
              />
              <p className="mt-1.5 text-xs text-earth-500">
                8+ characters with uppercase, lowercase, and a number
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Confirm new password"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={
                  loading || verificationCode.length < 6 || newPassword.length < 8
                }
                className="w-full btn-primary py-3 text-base"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Resetting Password...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setStep("email")}
                className="text-sm font-medium text-field hover:text-field-600 hover:underline"
              >
                Didn't get a code? Resend or change email
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-earth-50 flex flex-col justify-center items-center py-12 px-4">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="mx-auto w-14 h-14 bg-harvest rounded-full flex items-center justify-center shadow-card mb-5">
          <Mail className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-3xl text-earth-900">
          Forgot Password?
        </h1>
        <p className="mt-2 text-earth-600">
          No problem! Enter your email to receive a reset code.
        </p>
      </div>

      {/* Form Card */}
      <div className="w-full max-w-sm card p-8 animate-slide-up">
        {error && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-alert-urgent text-sm text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSendCode} className="space-y-5">
          <div>
            <label htmlFor="email" className="label">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
            <p className="mt-1.5 text-xs text-earth-500">
              We'll send a verification code to this email
            </p>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-base"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending Code...
                </div>
              ) : (
                "Send Reset Code"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Back to login link */}
      <div className="mt-6 w-full max-w-sm animate-fade-in" style={{ animationDelay: "100ms" }}>
        <div className="py-4 text-center">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="inline-flex items-center gap-2 font-medium text-field hover:text-field-600 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-earth-500">
          &copy; {new Date().getFullYear()} Waddington Park. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
